import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Select,
  Spacer,
  Spinner,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash, FaPlus, FaTrash } from 'react-icons/fa';
import axios from '../../axiosConfig';
import { useUser } from '../../contexts/UserContext';
import { createUserInContext, toBrainRoleName } from './userCreationApi';
import { getOrganizationsForUserManagement } from './userOrganizationLookup';
import { getDisplayRoleLabel } from '../../utils/displayRole';
import { getRoleChangeSummary } from '../../utils/rbacSafety';
import { getMutationError, getUserMutationToast } from '../../utils/userMutationFeedback';
import {
  canCreateUsersInContext,
  filterConsultantsForPolicy,
  filterNetworksForPolicy,
  filterOrganizationsForPolicy,
  getUserCreationPolicy,
  resolveAllowedRolesByScope,
  resolveAllowedScopes,
} from '../../utils/userCreationPermissions';

/**
 * Canonical RBAC scopes (match Brain/DB)
 * NOTE: these are "role scopes" from the Role table.
 */
const SCOPE_OPTIONS = [
  { value: 'app', label: 'Platform' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'network', label: 'Network' },
  { value: 'organization', label: 'School' },
];

// If Brain contains legacy / accidental roles, hide them from selection to reduce confusion.
const ROLE_NAME_BLOCKLIST = new Set([
  'global', // commonly mistaken as a scope, not a real permission role
]);

const emptyRoleCatalog = {
  app: [],
  consultant: [],
  network: [],
  organization: [],
};

const roleSignature = (role) =>
  [
    (role?.name || '').toString().toLowerCase(),
    (role?.scope || '').toString().toLowerCase(),
    Number(role?.organization_id) || '',
    Number(role?.network_id) || '',
    Number(role?.consultant_id) || '',
  ].join('::');

const rolesSignature = (roles) =>
  (Array.isArray(roles) ? roles : [])
    .map(roleSignature)
    .sort()
    .join('|');

const ORG_ADMIN_ROLE_NAMES = new Set(['organization_admin', 'org_admin', 'org_master']);
const ORG_USER_ROLE_NAMES = new Set(['organization_user', 'org_user']);

const getOrganizationRoleTier = (roleName) => {
  const normalized = (roleName || '').toString().toLowerCase().trim();
  if (ORG_ADMIN_ROLE_NAMES.has(normalized)) return 'admin';
  if (ORG_USER_ROLE_NAMES.has(normalized)) return 'user';
  return null;
};

const getOrganizationRoleSwapWarnings = (originalRoles, nextRoles) => {
  const originalByOrganization = new Map();
  const nextByOrganization = new Map();

  const collectTier = (roles, bucket) => {
    (Array.isArray(roles) ? roles : []).forEach((role) => {
      if ((role?.scope || '').toString().toLowerCase() !== 'organization') return;

      const organizationId = Number(role?.organization_id);
      const tier = getOrganizationRoleTier(role?.name);
      if (!organizationId || !tier || bucket.has(organizationId)) return;

      bucket.set(organizationId, {
        tier,
        organizationName: role?.organization?.name || null,
      });
    });
  };

  collectTier(originalRoles, originalByOrganization);
  collectTier(nextRoles, nextByOrganization);

  return Array.from(originalByOrganization.entries())
    .map(([organizationId, original]) => {
      const next = nextByOrganization.get(organizationId);
      if (!next || next.tier === original.tier) return null;

      return {
        organizationId,
        organizationName: next.organizationName || original.organizationName || `School #${organizationId}`,
        fromTier: original.tier,
        toTier: next.tier,
      };
    })
    .filter(Boolean);
};

const buildOrganizationRoleSwapWarning = (swaps) => {
  if (!Array.isArray(swaps) || swaps.length === 0) return null;

  const summary = swaps
    .map((swap) => {
      const fromLabel = swap.fromTier === 'admin' ? 'school admin' : 'school user';
      const toLabel = swap.toTier === 'admin' ? 'school admin' : 'school user';
      return `${swap.organizationName}: ${fromLabel} will be replaced with ${toLabel}.`;
    })
    .join(' ');

  return `Only one school role can exist per school. Saving will replace the existing role for the same school. ${summary} Continue?`;
};

/**
 * STRICT normalize for /roles endpoint.
 * Expected payload from Brain: Array<Role> where Role includes { name, scope }
 * We intentionally ignore other shapes to avoid accidental junk ingestion.
 */
const normalizeRoleCatalogFromIndex = (payload) => {
  const catalog = { ...emptyRoleCatalog };

  if (!Array.isArray(payload)) {
    return catalog;
  }

  payload.forEach((row) => {
    if (!row || typeof row !== 'object') return;

    const scope = (row.scope || '').toString().toLowerCase().trim();
    const name = (row.name || '').toString().toLowerCase().trim();
    if (!scope || !name) return;
    if (!Object.prototype.hasOwnProperty.call(catalog, scope)) return;
    if (ROLE_NAME_BLOCKLIST.has(name)) return;

    if (!catalog[scope].includes(name)) catalog[scope].push(name);
  });

  Object.keys(catalog).forEach((scope) => {
    catalog[scope] = [...catalog[scope]].sort();
  });

  return catalog;
};

/**
 * Normalize roles from API into a consistent shape for the UI.
 * Expected shape from Brain:
 *   { name, scope, organization_id?, network_id?, consultant_id? }
 */
const normalizeRoles = (roles) => {
  if (!Array.isArray(roles)) return [];
  const seen = new Set();

  const normalized = roles
    .map((r) => {
      const name = (r?.name || r?.role || '').toString().trim();
      const scope = (r?.scope || r?.scope_type || '').toString().trim();
      if (!name || !scope) return null;

      const item = {
        name,
        scope: scope.toLowerCase(),
        organization_id: r?.organization_id ?? r?.organization?.id ?? null,
        network_id: r?.network_id ?? r?.network?.id ?? null,
        consultant_id: r?.consultant_id ?? r?.consultant?.id ?? null,
        organization: r?.organization || null,
        network: r?.network || null,
        consultant: r?.consultant || null,
        cannot_remove_reason: r?.cannot_remove_reason ?? r?.remove_blocked_reason ?? '',
        remove_warning: r?.remove_warning ?? '',
      };

      const key = [
        item.name.toLowerCase(),
        item.scope,
        item.organization_id || '',
        item.network_id || '',
        item.consultant_id || '',
      ].join('::');

      if (seen.has(key)) return null;
      seen.add(key);
      return item;
    })
    .filter(Boolean);

  return normalized;
};

const UserForm = ({
  initialValues = {
    first_name: '',
    last_name: '',
    email: '',
  },
  basePath = '/app/users',
  scope = 'hke',
  context,
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.800');
  const mutedBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const { user } = useUser();

  const isEditing = Boolean(initialValues.id);
  const isEditingSelf = Boolean(isEditing && user && Number(initialValues.id) === Number(user.id));
  const creationPolicy = useMemo(() => getUserCreationPolicy(user), [user]);
  const isConsultantScoped = scope === 'consultant';
  const implicitConsultantId = isConsultantScoped && Number(user?.consultant_id) > 0 ? Number(user.consultant_id) : null;
  const scopedContext = useMemo(() => {
    if (context?.type) return context;
    if (isConsultantScoped && implicitConsultantId) {
      return { type: 'consultant', id: implicitConsultantId, namespace: 'consultant' };
    }
    return { type: 'hke' };
  }, [context, implicitConsultantId, isConsultantScoped]);
  const createContext = scopedContext;
  const canCreateUsers = useMemo(
    () => (!isEditing ? canCreateUsersInContext(user, createContext) : false),
    [createContext, isEditing, user]
  );
  const canManageRoles = Boolean(isEditing && !isEditingSelf && creationPolicy.canCreateUsers);
  const roleBuilderContext = scopedContext;
  const allowedRoleScopes = useMemo(
    () => resolveAllowedScopes(creationPolicy, roleBuilderContext),
    [creationPolicy, roleBuilderContext]
  );
  const allowedScopeOptions = useMemo(() => {
    return SCOPE_OPTIONS.filter((option) => allowedRoleScopes.includes(option.value));
  }, [allowedRoleScopes]);

  const [formValues, setFormValues] = useState({ ...initialValues });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Password change (self only)
  const [passwordValues, setPasswordValues] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // RBAC
  const [userRoles, setUserRoles] = useState(normalizeRoles(initialValues.roles || []));
  const [originalRoles, setOriginalRoles] = useState(normalizeRoles(initialValues.roles || []));
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isSavingRoles, setIsSavingRoles] = useState(false);
  const [availableRolesByScope, setAvailableRolesByScope] = useState({ ...emptyRoleCatalog });

  // Lookup data for scoped roles
  const [organizations, setOrganizations] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [consultants, setConsultants] = useState([]);

  // New role builder
  const [newRoleScope, setNewRoleScope] = useState('app');
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleOrganizationId, setNewRoleOrganizationId] = useState('');
  const [newRoleNetworkId, setNewRoleNetworkId] = useState('');
  const [newRoleConsultantId, setNewRoleConsultantId] = useState('');

  // Role builder field-level errors (ONLY for staging a new role; should never block "Update user")
  const [roleBuilderErrors, setRoleBuilderErrors] = useState({});

  const allowedRolesByScope = useMemo(
    () => resolveAllowedRolesByScope(creationPolicy, availableRolesByScope, roleBuilderContext),
    [availableRolesByScope, creationPolicy, roleBuilderContext]
  );
  const roleOptionsForScope = useMemo(() => {
    return allowedRolesByScope[newRoleScope] || [];
  }, [allowedRolesByScope, newRoleScope]);

  const filteredOrganizations = useMemo(
    () => filterOrganizationsForPolicy(organizations, creationPolicy, roleBuilderContext),
    [creationPolicy, organizations, roleBuilderContext]
  );
  const filteredNetworks = useMemo(
    () => filterNetworksForPolicy(networks, creationPolicy, roleBuilderContext),
    [creationPolicy, networks, roleBuilderContext]
  );
  const filteredConsultants = useMemo(
    () => filterConsultantsForPolicy(consultants, creationPolicy),
    [consultants, creationPolicy]
  );
  const implicitRoleConsultantId = useMemo(() => {
    if (newRoleScope !== 'consultant') return null;
    if (implicitConsultantId) return implicitConsultantId;
    if (filteredConsultants.length === 1) return Number(filteredConsultants[0].id);
    return null;
  }, [filteredConsultants, implicitConsultantId, newRoleScope]);
  const implicitRoleNetworkId = useMemo(() => {
    if (newRoleScope !== 'network') return null;
    if (roleBuilderContext?.type === 'network' && Number(roleBuilderContext?.id) > 0) {
      return Number(roleBuilderContext.id);
    }
    if (filteredNetworks.length !== 1) return null;
    return Number(filteredNetworks[0].id);
  }, [filteredNetworks, newRoleScope, roleBuilderContext]);
  const implicitRoleOrganizationId = useMemo(() => {
    if (newRoleScope !== 'organization') return null;
    if (filteredOrganizations.length !== 1) return null;
    return Number(filteredOrganizations[0].id);
  }, [filteredOrganizations, newRoleScope]);
  const hideConsultantSelector = Boolean(newRoleScope === 'consultant' && implicitRoleConsultantId);
  const hideNetworkSelector = Boolean(newRoleScope === 'network' && implicitRoleNetworkId);
  const hideOrganizationSelector = Boolean(newRoleScope === 'organization' && implicitRoleOrganizationId);

  useEffect(() => {
    if (allowedScopeOptions.some((option) => option.value === newRoleScope)) return;
    setNewRoleScope(allowedScopeOptions[0]?.value || 'consultant');
  }, [allowedScopeOptions, newRoleScope]);

  useEffect(() => {
    if (newRoleScope !== 'consultant' || !implicitRoleConsultantId) return;
    setNewRoleConsultantId(String(implicitRoleConsultantId));
  }, [implicitRoleConsultantId, newRoleScope]);

  const hasUnsavedRoleChanges = useMemo(
    () => rolesSignature(userRoles) !== rolesSignature(originalRoles),
    [userRoles, originalRoles]
  );
  const roleChangeSummary = useMemo(
    () => getRoleChangeSummary(originalRoles, userRoles),
    [originalRoles, userRoles]
  );

  useEffect(() => {
    setFormValues({ ...initialValues });
    setErrors({});
    const nextRoles = normalizeRoles(initialValues.roles || []);
    setUserRoles(nextRoles);
    setOriginalRoles(nextRoles);
  }, [initialValues]);

  useEffect(() => {
    if (!canManageRoles) return;

    const loadUserRoles = async () => {
      const roleResponse = await axios.get(`/users/${initialValues.id}/roles`);
      const payload = roleResponse?.data;
      const fetchedRoles = Array.isArray(payload) ? payload : payload?.roles || payload?.user_roles || [];
      return normalizeRoles(fetchedRoles);
    };

    const load = async () => {
      setIsLoadingRoles(true);
      try {
        const fetchedRoles = await loadUserRoles();
        setUserRoles(fetchedRoles);
        setOriginalRoles(fetchedRoles);
      } catch (e) {
        const fallbackRoles = normalizeRoles(initialValues.roles || []);
        setUserRoles(fallbackRoles);
        setOriginalRoles(fallbackRoles);
      } finally {
        setIsLoadingRoles(false);
      }

      // Lookup lists (best-effort)
      try {
        const [roleCatalogRes, orgRes, netRes, conRes] = await Promise.allSettled([
          axios.get('/roles'),
          getOrganizationsForUserManagement(roleBuilderContext),
          axios.get('/networks'),
          axios.get('/consultants'),
        ]);

        const roleCatalogPayload = roleCatalogRes.status === 'fulfilled' ? roleCatalogRes.value?.data : null;
        const orgPayload = orgRes.status === 'fulfilled' ? orgRes.value : [];
        const netPayload = netRes.status === 'fulfilled' ? netRes.value?.data : null;
        const conPayload = conRes.status === 'fulfilled' ? conRes.value?.data : null;

        // STRICT parsing: only accept /roles index array.
        const normalizedCatalog = normalizeRoleCatalogFromIndex(roleCatalogPayload);
        setAvailableRolesByScope(normalizedCatalog);

        setOrganizations(Array.isArray(orgPayload) ? orgPayload : []);
        setNetworks(Array.isArray(netPayload) ? netPayload : netPayload?.networks || []);
        const consultantsPayload = Array.isArray(conPayload) ? conPayload : conPayload?.consultants || [];
        setConsultants(consultantsPayload);
      } catch (e) {
        setAvailableRolesByScope({ ...emptyRoleCatalog });
        setOrganizations([]);
        setNetworks([]);
        setConsultants([]);
      }
    };

    load();
  }, [canManageRoles, initialValues.id, initialValues.roles, roleBuilderContext]);

  useEffect(() => {
    if (isEditing || !canCreateUsers) return;

    let isMounted = true;

    const load = async () => {
      try {
        const [roleCatalogRes, orgRes, netRes, conRes] = await Promise.allSettled([
          axios.get('/roles'),
          getOrganizationsForUserManagement(roleBuilderContext),
          axios.get('/networks'),
          axios.get('/consultants'),
        ]);

        if (!isMounted) return;

        const roleCatalogPayload = roleCatalogRes.status === 'fulfilled' ? roleCatalogRes.value?.data : null;
        const orgPayload = orgRes.status === 'fulfilled' ? orgRes.value : [];
        const netPayload = netRes.status === 'fulfilled' ? netRes.value?.data : null;
        const conPayload = conRes.status === 'fulfilled' ? conRes.value?.data : null;

        setAvailableRolesByScope(normalizeRoleCatalogFromIndex(roleCatalogPayload));
        setOrganizations(Array.isArray(orgPayload) ? orgPayload : []);
        setNetworks(Array.isArray(netPayload) ? netPayload : netPayload?.networks || []);
        setConsultants(Array.isArray(conPayload) ? conPayload : conPayload?.consultants || []);
      } catch (e) {
        if (!isMounted) return;
        setAvailableRolesByScope({ ...emptyRoleCatalog });
        setOrganizations([]);
        setNetworks([]);
        setConsultants([]);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [canCreateUsers, isEditing, roleBuilderContext]);

  useEffect(() => {
    if (newRoleScope !== 'network' || !implicitRoleNetworkId) return;
    setNewRoleNetworkId(String(implicitRoleNetworkId));
  }, [implicitRoleNetworkId, newRoleScope]);

  useEffect(() => {
    if (newRoleScope !== 'organization' || !implicitRoleOrganizationId) return;
    setNewRoleOrganizationId(String(implicitRoleOrganizationId));
  }, [implicitRoleOrganizationId, newRoleScope]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value || '' }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      if (isEditing) {
        // Profile update only — RBAC is managed separately via Save roles
        const payload = {
          first_name: formValues.first_name,
          last_name: formValues.last_name,
          email: formValues.email,
          theme: formValues.theme,
          language: formValues.language,
          email_notifications:
            formValues.email_notifications ??
            formValues?.settings?.email_notifications ??
            formValues?.settings?.emailNotifications,
          force_password_reset: formValues.force_password_reset,
          status: formValues.status,
        };

        Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

        await axios.patch(`/users/${initialValues.id}`, { user: payload });

        toast(getUserMutationToast('update'));
      } else {
        if (!canCreateUsers) {
          toast({
            title: 'Not allowed',
            description: 'You do not have permission to create users here.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
          setIsSubmitting(false);
          return;
        }

        const createValidationErrors = {};
        const effectiveConsultantId = implicitRoleConsultantId ? String(implicitRoleConsultantId) : newRoleConsultantId;
        const effectiveNetworkId = implicitRoleNetworkId ? String(implicitRoleNetworkId) : newRoleNetworkId;
        const effectiveOrganizationId = implicitRoleOrganizationId ? String(implicitRoleOrganizationId) : newRoleOrganizationId;

        if (!newRoleName) createValidationErrors.role = ['Please select a role'];
        if (newRoleScope === 'consultant' && !effectiveConsultantId) createValidationErrors.consultant = ['Please select a consultant'];
        if (newRoleScope === 'network' && !effectiveNetworkId) createValidationErrors.network = ['Please select a network'];
        if (newRoleScope === 'organization' && !effectiveOrganizationId) createValidationErrors.organization = ['Please select a school'];

        if (Object.keys(createValidationErrors).length > 0) {
          setRoleBuilderErrors(createValidationErrors);
          setIsSubmitting(false);
          return;
        }

        await createUserInContext(
          createContext,
          {
            first_name: formValues.first_name,
            last_name: formValues.last_name,
            email: formValues.email,
            role_name: newRoleName,
            scope: newRoleScope,
            consultant_id: newRoleScope === 'consultant' ? Number(effectiveConsultantId) : null,
            network_id: newRoleScope === 'network' ? Number(effectiveNetworkId) : null,
            organization_id: newRoleScope === 'organization' ? Number(effectiveOrganizationId) : null,
          }
        );

        toast(getUserMutationToast('create'));
      }

      navigate(basePath);
    } catch (error) {
      const { fieldErrors, message } = getMutationError(error);
      setErrors(fieldErrors);
      toast({
        title: 'Could not save user',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordFieldChange = (event) => {
    const { name, value } = event.target;
    setPasswordValues((prev) => ({ ...prev, [name]: value || '' }));
    if (passwordErrors[name]) setPasswordErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handlePasswordChange = async () => {
    if (!isEditingSelf) return;

    setPasswordErrors({});
    if (!passwordValues.current_password) return setPasswordErrors({ current_password: ['Current password is required'] });
    if (!passwordValues.password) return setPasswordErrors({ password: ['New password is required'] });
    if (passwordValues.password !== passwordValues.password_confirmation) {
      return setPasswordErrors({ password_confirmation: ['Passwords do not match'] });
    }

    setIsChangingPassword(true);
    try {
      await axios.patch('/users/me/change_password', {
        current_password: passwordValues.current_password,
        password: passwordValues.password,
        password_confirmation: passwordValues.password_confirmation,
      });

      toast({
        title: 'Password updated',
        description: 'Your password has been changed.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      setPasswordValues({ current_password: '', password: '', password_confirmation: '' });
    } catch (error) {
      const { fieldErrors, message } = getMutationError(error);
      setPasswordErrors(fieldErrors);
      toast({
        title: 'Could not update password',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const resetNewRoleFields = (scope) => {
    setNewRoleScope(scope);
    setNewRoleName('');
    setNewRoleOrganizationId('');
    setNewRoleNetworkId('');
    setNewRoleConsultantId('');
    setRoleBuilderErrors({});
  };

  const roleKey = (r) =>
    [
      (r?.name || '').toString().toLowerCase(),
      (r?.scope || '').toString().toLowerCase(),
      r?.organization_id || '',
      r?.network_id || '',
      r?.consultant_id || '',
    ].join('::');

  const addRole = () => {
    // IMPORTANT: Do not rely on HTML "required" for these selects, because this entire component is inside a <form>.
    // We validate ONLY when staging a new role.
    const nextErrors = {};

    const effectiveConsultantId = implicitRoleConsultantId ? String(implicitRoleConsultantId) : newRoleConsultantId;
    const effectiveNetworkId = implicitRoleNetworkId ? String(implicitRoleNetworkId) : newRoleNetworkId;
    const effectiveOrganizationId = implicitRoleOrganizationId ? String(implicitRoleOrganizationId) : newRoleOrganizationId;

    if (!newRoleName) nextErrors.role = ['Please select a role'];
    if (newRoleScope === 'organization' && !effectiveOrganizationId) nextErrors.organization = ['Please select a school'];
    if (newRoleScope === 'network' && !effectiveNetworkId) nextErrors.network = ['Please select a network'];

    if (newRoleScope === 'consultant' && !effectiveConsultantId) nextErrors.consultant = ['Please select a consultant'];

    if (Object.keys(nextErrors).length > 0) {
      setRoleBuilderErrors(nextErrors);
      return;
    }

    setRoleBuilderErrors({});

    const next = {
      name: newRoleName,
      scope: newRoleScope,
      organization_id: null,
      network_id: null,
      consultant_id: null,
      organization: null,
      network: null,
      consultant: null,
    };

    if (newRoleScope === 'organization') {
      next.organization_id = Number(effectiveOrganizationId);
      next.organization = filteredOrganizations.find((o) => Number(o.id) === Number(effectiveOrganizationId)) || null;
    }

    if (newRoleScope === 'network') {
      next.network_id = Number(effectiveNetworkId);
      next.network = filteredNetworks.find((n) => Number(n.id) === Number(effectiveNetworkId)) || null;
    }

    if (newRoleScope === 'consultant') {
      next.consultant_id = Number(effectiveConsultantId);
      next.consultant = filteredConsultants.find((c) => Number(c.id) === Number(effectiveConsultantId)) || null;
    }

    const exists = userRoles.some((r) => roleKey(r) === roleKey(next));
    if (exists) return;

    setUserRoles((prev) => [...prev, next]);
    setNewRoleName('');
    setNewRoleOrganizationId('');
    setNewRoleNetworkId('');
    setNewRoleConsultantId('');
  };

  const removeRole = (idx) => {
    const role = userRoles[idx];
    if (role?.cannot_remove_reason) {
      toast({
        title: 'Role removal not allowed',
        description: role.cannot_remove_reason,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const scopeLabel = SCOPE_OPTIONS.find((scopeOption) => scopeOption.value === role?.scope)?.label || role?.scope || 'Scope';
    const roleLabel = getDisplayRoleLabel(role?.name || 'role');
    const contextLabel =
      role?.organization?.name ||
      role?.network?.name ||
      role?.consultant?.name ||
      (role?.organization_id ? `School #${role.organization_id}` : '') ||
      (role?.network_id ? `Network #${role.network_id}` : '') ||
      (role?.consultant_id ? `Consultant #${role.consultant_id}` : '');
    const confirmMessage = [
      `Remove ${roleLabel} (${scopeLabel}${contextLabel ? `: ${contextLabel}` : ''})?`,
      "This won't take effect until you save.",
    ].join(' ');
    if (!window.confirm(confirmMessage)) return;
    setUserRoles((prev) => prev.filter((_, i) => i !== idx));
  };

  const refreshUserRoles = async () => {
    if (!isEditing) return;
    const roleResponse = await axios.get(`/users/${initialValues.id}/roles`);
    const payload = roleResponse?.data;
    const fetchedRoles = Array.isArray(payload) ? payload : payload?.roles || payload?.user_roles || [];
    const normalized = normalizeRoles(fetchedRoles);
    setUserRoles(normalized);
    setOriginalRoles(normalized);
  };

  const saveRoles = async () => {
    if (!isEditing) return;
    if (!hasUnsavedRoleChanges) return;

    const changeSummaryParts = [];
    if (roleChangeSummary.summaryLabel) {
      changeSummaryParts.push(`Role changes: ${roleChangeSummary.summaryLabel}.`);
    }
    if (roleChangeSummary.warnings.length > 0) {
      changeSummaryParts.push(...roleChangeSummary.warnings);
    }

    const organizationRoleSwapWarning = buildOrganizationRoleSwapWarning(
      getOrganizationRoleSwapWarnings(originalRoles, userRoles)
    );
    const confirmMessage = changeSummaryParts
      .concat(organizationRoleSwapWarning ? [organizationRoleSwapWarning] : [])
      .concat(['Save these role changes?'])
      .join(' ');
    if (!window.confirm(confirmMessage)) return;

    setIsSavingRoles(true);
    try {
      const payload = userRoles.map((r) => ({
        name: toBrainRoleName(r.name, r.scope),
        scope: r.scope,
        organization_id: r.scope === 'organization' ? r.organization_id : null,
        network_id: r.scope === 'network' ? r.network_id : null,
        consultant_id: r.scope === 'consultant' ? r.consultant_id : null,
      }));

      await axios.patch(`/users/${initialValues.id}/roles`, { roles: payload });
      await refreshUserRoles();

      toast(getUserMutationToast('roles'));
    } catch (error) {
      const { message } = getMutationError(error);
      toast({
        title: 'Could not update roles',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSavingRoles(false);
    }
  };

  if (!user) return <Text>Loading...</Text>;

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" shadow="md">
        <CardHeader pb={0}>
          <Text fontSize="xl" fontWeight="bold">
            {isEditing ? 'Edit user' : 'Create user'}
          </Text>
          <Text color="gray.500" fontSize="sm">
            Profile details and access control are managed separately.
          </Text>
        </CardHeader>

        <CardBody>
          <VStack spacing={5} align="stretch">
            <VStack spacing={4} align="stretch">
              {!isEditing && !canCreateUsers ? (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>You do not have permission to create users from this page.</AlertDescription>
                </Alert>
              ) : null}

              <FormControl isRequired isInvalid={Boolean(errors.first_name)}>
                <FormLabel>First name</FormLabel>
                <Input name="first_name" value={formValues.first_name || ''} onChange={handleInputChange} />
                <FormErrorMessage>{errors.first_name?.[0]}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={Boolean(errors.last_name)}>
                <FormLabel>Last name</FormLabel>
                <Input name="last_name" value={formValues.last_name || ''} onChange={handleInputChange} />
                <FormErrorMessage>{errors.last_name?.[0]}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={Boolean(errors.email)}>
                <FormLabel>Email</FormLabel>
                <Input type="email" name="email" value={formValues.email || ''} onChange={handleInputChange} />
                <FormErrorMessage>{errors.email?.[0]}</FormErrorMessage>
              </FormControl>
            </VStack>

            {!isEditing && canCreateUsers && (
              <>
                <Divider />
                <Box p={4} borderRadius="md" bg={mutedBg}>
                  <VStack align="stretch" spacing={4}>
                    <Text fontWeight="bold">Access role</Text>
                    <HStack spacing={3} align="end" flexWrap="wrap">
                      <FormControl maxW="220px">
                        <FormLabel mb={1}>Scope</FormLabel>
                        <Select value={newRoleScope} onChange={(e) => resetNewRoleFields(e.target.value)}>
                          {allowedScopeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl maxW="260px" isInvalid={Boolean(roleBuilderErrors.role)}>
                        <FormLabel mb={1}>Role</FormLabel>
                        <Select
                          placeholder={roleOptionsForScope.length > 0 ? 'Select role' : 'No roles available'}
                          value={newRoleName}
                          onChange={(e) => {
                            setNewRoleName(e.target.value);
                            if (roleBuilderErrors.role) {
                              setRoleBuilderErrors((prev) => ({ ...prev, role: null }));
                            }
                          }}
                          isDisabled={roleOptionsForScope.length === 0}
                        >
                          {roleOptionsForScope.map((name) => (
                            <option key={name} value={name}>
                              {getDisplayRoleLabel(name)}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>{roleBuilderErrors.role?.[0]}</FormErrorMessage>
                      </FormControl>

                      {newRoleScope === 'consultant' && !hideConsultantSelector && (
                        <FormControl maxW="280px" isInvalid={Boolean(roleBuilderErrors.consultant)}>
                          <FormLabel mb={1}>Consultant</FormLabel>
                          <Select
                            placeholder="Select consultant"
                            value={newRoleConsultantId}
                            onChange={(e) => {
                              setNewRoleConsultantId(e.target.value);
                              if (roleBuilderErrors.consultant) {
                                setRoleBuilderErrors((prev) => ({ ...prev, consultant: null }));
                              }
                            }}
                          >
                            {filteredConsultants.map((consultant) => (
                              <option key={consultant.id} value={consultant.id}>
                                {consultant.name}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage>{roleBuilderErrors.consultant?.[0]}</FormErrorMessage>
                        </FormControl>
                      )}

                      {newRoleScope === 'network' && !hideNetworkSelector && (
                        <FormControl maxW="280px" isInvalid={Boolean(roleBuilderErrors.network)}>
                          <FormLabel mb={1}>Network</FormLabel>
                          <Select
                            placeholder="Select network"
                            value={newRoleNetworkId}
                            onChange={(e) => {
                              setNewRoleNetworkId(e.target.value);
                              if (roleBuilderErrors.network) {
                                setRoleBuilderErrors((prev) => ({ ...prev, network: null }));
                              }
                            }}
                          >
                            {filteredNetworks.map((network) => (
                              <option key={network.id} value={network.id}>
                                {network.name}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage>{roleBuilderErrors.network?.[0]}</FormErrorMessage>
                        </FormControl>
                      )}

                      {newRoleScope === 'organization' && !hideOrganizationSelector && (
                        <FormControl maxW="280px" isInvalid={Boolean(roleBuilderErrors.organization)}>
                          <FormLabel mb={1}>School</FormLabel>
                          <Select
                            placeholder="Select school"
                            value={newRoleOrganizationId}
                            onChange={(e) => {
                              setNewRoleOrganizationId(e.target.value);
                              if (roleBuilderErrors.organization) {
                                setRoleBuilderErrors((prev) => ({ ...prev, organization: null }));
                              }
                            }}
                          >
                            {filteredOrganizations.map((organization) => (
                              <option key={organization.id} value={organization.id}>
                                {organization.name}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage>{roleBuilderErrors.organization?.[0]}</FormErrorMessage>
                        </FormControl>
                      )}
                    </HStack>
                  </VStack>
                </Box>
              </>
            )}

            {isEditing && isEditingSelf && (
              <>
                <Divider />
                <Box p={4} borderRadius="md" bg={mutedBg}>
                  <VStack align="stretch" spacing={3}>
                    <Text fontWeight="bold">Change password</Text>

                    <FormControl isRequired isInvalid={Boolean(passwordErrors.current_password)}>
                      <FormLabel mb={1}>Current password</FormLabel>
                      <InputGroup>
                        <Input
                          type={showCurrentPassword ? 'text' : 'password'}
                          name="current_password"
                          value={passwordValues.current_password}
                          onChange={handlePasswordFieldChange}
                        />
                        <InputRightElement>
                          <IconButton
                            icon={showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                            onClick={() => setShowCurrentPassword((v) => !v)}
                            aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                            variant="ghost"
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>{passwordErrors.current_password?.[0]}</FormErrorMessage>
                    </FormControl>

                    <FormControl isRequired isInvalid={Boolean(passwordErrors.password)}>
                      <FormLabel mb={1}>New password</FormLabel>
                      <InputGroup>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={passwordValues.password}
                          onChange={handlePasswordFieldChange}
                        />
                        <InputRightElement>
                          <IconButton
                            icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            variant="ghost"
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>{passwordErrors.password?.[0]}</FormErrorMessage>
                    </FormControl>

                    <FormControl isRequired isInvalid={Boolean(passwordErrors.password_confirmation)}>
                      <FormLabel mb={1}>Confirm new password</FormLabel>
                      <InputGroup>
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          name="password_confirmation"
                          value={passwordValues.password_confirmation}
                          onChange={handlePasswordFieldChange}
                        />
                        <InputRightElement>
                          <IconButton
                            icon={showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            onClick={() => setShowConfirmPassword((v) => !v)}
                            aria-label={showConfirmPassword ? 'Hide confirmation' : 'Show confirmation'}
                            variant="ghost"
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>{passwordErrors.password_confirmation?.[0]}</FormErrorMessage>
                    </FormControl>

                    <Flex justify="flex-end">
                      <Button
                        type="button"
                        variant="outline"
                        colorScheme="blue"
                        onClick={handlePasswordChange}
                        isLoading={isChangingPassword}
                      >
                        Change password
                      </Button>
                    </Flex>
                  </VStack>
                </Box>
              </>
            )}

            {canManageRoles && (
              <>
                <Divider />
                <Box p={4} borderRadius="md" bg={mutedBg}>
                  <VStack align="stretch" spacing={4}>
                    <Text fontWeight="bold">Access roles</Text>

                    {hasUnsavedRoleChanges && (
                      <Alert status={roleChangeSummary.downgraded.length > 0 ? 'warning' : 'info'} borderRadius="md">
                        <AlertIcon />
                        <AlertDescription>
                          <Text fontSize="sm">You have staged role changes. They take effect only after saving.</Text>
                          {roleChangeSummary.summaryLabel ? (
                            <Text fontSize="sm" fontWeight="medium">
                              Summary: {roleChangeSummary.summaryLabel}.
                            </Text>
                          ) : null}
                          {roleChangeSummary.warnings.map((warning) => (
                            <Text key={warning} fontSize="sm">
                              {warning}
                            </Text>
                          ))}
                        </AlertDescription>
                      </Alert>
                    )}

                    {isLoadingRoles ? (
                      <HStack py={2}>
                        <Spinner size="sm" />
                        <Text fontSize="sm">Loading roles…</Text>
                      </HStack>
                    ) : (
                      <List spacing={2}>
                        {userRoles.length === 0 ? (
                          <ListItem>
                            <Text fontSize="sm" color="gray.500">No roles assigned.</Text>
                          </ListItem>
                        ) : (
                          userRoles.map((r, idx) => {
                            const scopeLabel = SCOPE_OPTIONS.find((s) => s.value === r.scope)?.label || r.scope;

                            const ctxLabel =
                              r.scope === 'organization'
                                ? (r.organization?.name || `School #${r.organization_id}`)
                                : r.scope === 'network'
                                  ? (r.network?.name || `Network #${r.network_id}`)
                                  : r.scope === 'consultant'
                                    ? (r.consultant?.name || `Consultant #${r.consultant_id}`)
                                    : null;

                            return (
                              <ListItem key={`${roleKey(r)}-${idx}`}>
                                <HStack justify="space-between" align="start">
                                  <HStack spacing={2} flexWrap="wrap">
                                    <Tag colorScheme="blue" variant="subtle">
                                      <TagLabel>{getDisplayRoleLabel(r.name)}</TagLabel>
                                    </Tag>
                                    <Tag colorScheme="gray" variant="subtle">
                                      <TagLabel>{scopeLabel}</TagLabel>
                                    </Tag>
                                    {ctxLabel && (
                                      <Tag colorScheme="green" variant="subtle">
                                        <TagLabel>{ctxLabel}</TagLabel>
                                      </Tag>
                                    )}
                                  </HStack>

                                  <Tooltip label={r.cannot_remove_reason || r.remove_warning} hasArrow isDisabled={!r.cannot_remove_reason && !r.remove_warning}>
                                    <Box as="span">
                                      <IconButton
                                        icon={<FaTrash />}
                                        aria-label="Remove role"
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="red"
                                        onClick={() => removeRole(idx)}
                                        isDisabled={Boolean(r.cannot_remove_reason)}
                                      />
                                    </Box>
                                  </Tooltip>
                                </HStack>
                              </ListItem>
                            );
                          })
                        )}
                      </List>
                    )}

                    <Divider />

                    <VStack align="stretch" spacing={3}>
                      <HStack spacing={3} align="end" flexWrap="wrap">
                        <FormControl maxW="220px">
                          <FormLabel mb={1}>Scope</FormLabel>
                          <Select
                            value={newRoleScope}
                            onChange={(e) => resetNewRoleFields(e.target.value)}
                          >
                            {allowedScopeOptions.map((s) => (
                              <option key={s.value} value={s.value}>
                                {s.label}
                              </option>
                            ))}
                          </Select>
                        </FormControl>

                        {/* NOT required via HTML; validate only when pressing "Stage role" */}
                        <FormControl maxW="260px" isInvalid={Boolean(roleBuilderErrors.role)}>
                          <FormLabel mb={1}>Role</FormLabel>
                          <Select
                            placeholder={roleOptionsForScope.length > 0 ? 'Select role' : 'No roles available'}
                            value={newRoleName}
                            onChange={(e) => {
                              setNewRoleName(e.target.value);
                              if (roleBuilderErrors.role) {
                                setRoleBuilderErrors((prev) => ({ ...prev, role: null }));
                              }
                            }}
                            isDisabled={roleOptionsForScope.length === 0}
                          >
                            {roleOptionsForScope.map((name) => (
                              <option key={name} value={name}>
                                {getDisplayRoleLabel(name)}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage>{roleBuilderErrors.role?.[0]}</FormErrorMessage>
                        </FormControl>

                        {newRoleScope === 'organization' && !hideOrganizationSelector && (
                          <FormControl maxW="280px" isInvalid={Boolean(roleBuilderErrors.organization)}>
                            <FormLabel mb={1}>School</FormLabel>
                            <Select
                              placeholder="Select school"
                              value={newRoleOrganizationId}
                              onChange={(e) => {
                                setNewRoleOrganizationId(e.target.value);
                                if (roleBuilderErrors.organization) {
                                  setRoleBuilderErrors((prev) => ({ ...prev, organization: null }));
                                }
                              }}
                            >
                              {filteredOrganizations.map((o) => (
                                <option key={o.id} value={o.id}>
                                  {o.name}
                                </option>
                              ))}
                            </Select>
                            <FormErrorMessage>{roleBuilderErrors.organization?.[0]}</FormErrorMessage>
                          </FormControl>
                        )}

                        {newRoleScope === 'network' && !hideNetworkSelector && (
                          <FormControl maxW="280px" isInvalid={Boolean(roleBuilderErrors.network)}>
                            <FormLabel mb={1}>Network</FormLabel>
                            <Select
                              placeholder="Select network"
                              value={newRoleNetworkId}
                              onChange={(e) => {
                                setNewRoleNetworkId(e.target.value);
                                if (roleBuilderErrors.network) {
                                  setRoleBuilderErrors((prev) => ({ ...prev, network: null }));
                                }
                              }}
                            >
                              {filteredNetworks.map((n) => (
                                <option key={n.id} value={n.id}>
                                  {n.name}
                                </option>
                              ))}
                            </Select>
                            <FormErrorMessage>{roleBuilderErrors.network?.[0]}</FormErrorMessage>
                          </FormControl>
                        )}

                        {newRoleScope === 'consultant' && !hideConsultantSelector && (
                          <FormControl maxW="280px" isInvalid={Boolean(roleBuilderErrors.consultant)}>
                            <FormLabel mb={1}>Consultant</FormLabel>
                            <Select
                              placeholder="Select consultant"
                              value={newRoleConsultantId}
                              onChange={(e) => {
                                setNewRoleConsultantId(e.target.value);
                                if (roleBuilderErrors.consultant) {
                                  setRoleBuilderErrors((prev) => ({ ...prev, consultant: null }));
                                }
                              }}
                            >
                              {filteredConsultants.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </Select>
                            <FormErrorMessage>{roleBuilderErrors.consultant?.[0]}</FormErrorMessage>
                          </FormControl>
                        )}

                        <Button
                          type="button"
                          leftIcon={<FaPlus />}
                          onClick={addRole}
                          variant="outline"
                          colorScheme="blue"
                          isDisabled={
                            roleOptionsForScope.length === 0
                          }
                        >
                          Stage role
                        </Button>
                      </HStack>

                      <Flex>
                        <Spacer />
                        <Button
                          type="button"
                          variant="outline"
                          colorScheme="blue"
                          onClick={saveRoles}
                          isLoading={isSavingRoles}
                          isDisabled={!hasUnsavedRoleChanges}
                        >
                          Save role changes
                        </Button>
                      </Flex>
                    </VStack>
                  </VStack>
                </Box>
              </>
            )}

            <Flex>
              <Button type="button" variant="outline" onClick={() => navigate(basePath)}>
                Cancel
              </Button>
              <Spacer />
              <Button
                type="submit"
                colorScheme="green"
                variant="outline"
                isLoading={isSubmitting}
                isDisabled={!isEditing && !canCreateUsers}
              >
                {isEditing ? 'Update user' : 'Create user'}
              </Button>
            </Flex>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default UserForm;
